import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';

interface TierLimitModalProps {
  open: boolean;
  onClose: () => void;
  resourceName: string; // "printers", "filaments", etc.
  resourceLabel: string; // "Printers", "Filaments", etc.
  currentCount: number;
  maxCount: number;
  tierName: string; // "Tinkerer", "Hobbyist", "Business"
  message?: string; // Optional custom message
}

export function TierLimitModal({
  open,
  onClose,
  resourceName,
  resourceLabel,
  currentCount,
  maxCount,
  tierName,
  message,
}: TierLimitModalProps) {
  const navigate = useNavigate();

  const handleUpgrade = () => {
    navigate('/settings/subscription');
    onClose();
  };

  const defaultMessage = `You cannot add more ${resourceName} on the ${tierName} tier. To add more than ${maxCount}, upgrade your plan.`;
  const displayMessage = message || defaultMessage;

  return (
    <Modal open={open} onClose={onClose}>
      <div className="flex flex-col gap-6">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-warning/10">
            <AlertTriangle className="h-6 w-6 text-warning" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-foreground">
              {tierName} Tier Limit Reached
            </h2>
            <p className="mt-1 text-sm text-foreground-muted">
              {displayMessage}
            </p>
          </div>
        </div>

        {/* Metric Display */}
        <div className="rounded-lg border border-border bg-background-subtle p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-foreground-muted">Current {resourceLabel}</p>
              <p className="text-2xl font-semibold text-foreground">{currentCount}</p>
            </div>
            <div className="text-foreground-muted">
              <svg
                className="h-8 w-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm text-foreground-muted">Max {tierName} {resourceLabel}</p>
              <p className="text-2xl font-semibold text-foreground">{maxCount}</p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpgrade}>
            Upgrade Now
          </Button>
        </div>
      </div>
    </Modal>
  );
}
